/**
 * Widget Loader
 * Handles loading of external MFN/Datablocks widgets and loading indicators
 */

/**
 * Load external widget script
 * @param {string} widgetUrl - The widget API URL
 */
function loadWidgetScript(widgetUrl) {
    if (!window._MF) {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.src = widgetUrl + '/assets/js/loader-v5.js';
        document.body.appendChild(script);
    }
    
    window._MF = window._MF || {
        data: [],
        url: widgetUrl,
        ready: false,
        render: function() { window._MF.ready = true; },
        push: function(w) { window._MF.data.push(w); }
    };
}

/**
 * Initialize a widget
 * @param {string} widgetId - The widget container ID
 * @param {string} widgetType - The type of widget to load
 * @param {string} locale - The locale/language code
 * @param {string} token - The widget token
 */
function initWidget(widgetId, widgetType, locale, token) {
    if (!window._MF) {
        console.error('Widget loader not initialized');
        return;
    }
    
    window._MF.push({
        query: '#' + widgetId,
        widget: widgetType,
        locale: locale,
        token: token
    });
}

/**
 * Check if a string contains a date
 * @param {string} text - The text to check
 * @returns {boolean} True if the text appears to be a date
 */
function isDateString(text) {
    if (!text || typeof text !== 'string') return false;
    
    const trimmed = text.trim();
    if (trimmed.length === 0) return false;
    
    // ISO format: YYYY-MM-DD or YYYY/MM/DD
    const isoPattern = /^\d{4}[-/]\d{1,2}[-/]\d{1,2}$/;
    
    // Common date patterns: DD-MM-YYYY, DD/MM/YYYY, MM-DD-YYYY, MM/DD/YYYY
    const commonPattern = /^\d{1,2}[-/.]\d{1,2}[-/.]\d{2,4}$/;
    
    // Month name formats: Jan 1, 2025 | 1 Jan 2025 | January 1, 2025
    const monthNamePattern = /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec|January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},?\s+\d{4}$|^\d{1,2}\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec|January|February|March|April|May|June|July|August|September|October|November|December),?\s+\d{4}$/i;
    
    return isoPattern.test(trimmed) || commonPattern.test(trimmed) || monthNamePattern.test(trimmed);
}

/**
 * Add date class to table cells containing dates
 * @param {HTMLElement} container - The widget container element
 */
function addDateClassesToCells(container) {
    const tableCells = container.querySelectorAll('td');
    
    tableCells.forEach(cell => {
        const cellText = cell.textContent || cell.innerText;
        
        if (isDateString(cellText)) {
            cell.classList.add('td-date');
        }
    });
}

/**
 * Check if widget has loaded and hide loading indicator
 * @param {string} widgetId - The widget container ID
 * @param {string} loadingId - The loading indicator ID
 */
function checkWidgetLoaded(widgetId, loadingId) {
    const container = document.getElementById(widgetId);
    const loadingIndicator = document.getElementById(loadingId);
    
    if (container && container.children.length > 0) {
        // Add fade-in class to container first
        container.classList.add('widget-loaded');
        
        // Add date classes to table cells
        addDateClassesToCells(container);
        
        // Hide loading indicator after a brief delay
        setTimeout(() => {
            if (loadingIndicator) {
                loadingIndicator.style.opacity = '0';
                setTimeout(() => {
                    loadingIndicator.style.display = 'none';
                }, 300);
            }
        }, 100);
    } else {
        setTimeout(() => checkWidgetLoaded(widgetId, loadingId), 100);
    }
}

/**
 * Initialize all widgets on the page
 */
export function initWidgets() {
    const widgetContainers = document.querySelectorAll('[data-widget-container]');
    
    if (widgetContainers.length === 0) return;
    
    widgetContainers.forEach(container => {
        const widgetUrl = container.dataset.widgetUrl;
        const widgetId = container.dataset.widgetId;
        const loadingId = container.dataset.loadingId;
        const widgetType = container.dataset.widgetType;
        const locale = container.dataset.locale;
        const token = container.dataset.token;
        
        if (!widgetUrl || !widgetId || !widgetType || !token) {
            console.error('Widget: Missing required data attributes', container);
            return;
        }
        
        // Load the widget script (only once per URL)
        loadWidgetScript(widgetUrl);
        
        // Initialize the specific widget
        initWidget(widgetId, widgetType, locale, token);
        
        // Start checking for widget load completion
        if (loadingId) {
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', () => checkWidgetLoaded(widgetId, loadingId));
            } else {
                checkWidgetLoaded(widgetId, loadingId);
            }
        }
    });
}
